import React, { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { logService } from "../utils/logService";

const LogList = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const { user, isAdmin, isModerator } = useAuth();

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        const logsData = await logService.getAllLogs();
        setLogs(logsData);
      } catch (error) {
        console.error("Error fetching logs:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "—";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "Agent Created":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
        );
      case "Agent Status Changed":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
            />
          </svg>
        );
      case "Validation Added":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        );
      case "User Added by Admin":
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
            />
          </svg>
        );
      default:
        return (
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
        );
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "Agent Created":
        return "bg-valia-accent";
      case "Agent Status Changed":
        return "bg-valia-secondary";
      case "Validation Added":
        return "bg-valia-warning";
      case "User Added by Admin":
        return "bg-valia-primary";
      default:
        return "bg-valia-gray-600";
    }
  };

  // Filter and sort logs
  const filteredAndSortedLogs = logs
    .filter((log) => {
      const matchesFilter =
        filterAction === "all" || log.action === filterAction;

      return matchesFilter;
    })
    .sort((a, b) => {
      const timestampA = a.timestamp?.toDate
        ? a.timestamp.toDate()
        : new Date(a.timestamp);
      const timestampB = b.timestamp?.toDate
        ? b.timestamp.toDate()
        : new Date(b.timestamp);

      return sortBy === "newest"
        ? timestampB - timestampA
        : timestampA - timestampB;
    });

  const uniqueActions = [...new Set(logs.map((log) => log.action))];

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="valia-spinner h-12 w-12 mx-auto mb-4"></div>
          <span className="text-valia-gray-600 text-lg">Loading logs...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-valia-primary mb-2">
              System Activity Log
            </h1>
            <p className="text-valia-gray-600 text-base">
              Comprehensive audit trail of all system activities
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-2xl font-bold text-valia-primary">
                {logs.length}
              </div>
              <div className="text-sm text-valia-gray-600">Total Events</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="valia-card mb-6">
        <div className="valia-card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Action Filter */}
            <div>
              <label className="valia-label">Filter by Action</label>
              <select
                value={filterAction}
                onChange={(e) => setFilterAction(e.target.value)}
                className="valia-input"
              >
                <option value="all">All Actions</option>
                {uniqueActions.map((action) => (
                  <option key={action} value={action}>
                    {action}
                  </option>
                ))}
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="valia-label">Sort by Date</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="valia-input"
              >
                <option value="newest">Newest First</option>
                <option value="oldest">Oldest First</option>
              </select>
            </div>
          </div>

          {/* Results Summary */}
          <div className="mt-4 pt-4 border-t border-valia-gray-200">
            <div className="flex items-center justify-between text-sm text-valia-gray-600">
              <span>
                Showing {filteredAndSortedLogs.length} of {logs.length} events
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Logs List */}
      <div className="space-y-4">
        {filteredAndSortedLogs.length === 0 ? (
          <div className="text-center text-valia-gray-600 py-12">
            <svg
              className="w-12 h-12 sm:w-16 sm:h-16 text-valia-gray-400 mx-auto mb-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <p className="text-base sm:text-lg mb-4">
              {logs.length === 0
                ? "No logs found."
                : "No logs match the selected filter."}
            </p>
          </div>
        ) : (
          filteredAndSortedLogs.map((log, index) => {
            const timestamp = formatTimestamp(log.timestamp);
            return (
              <div
                key={log.id}
                className="valia-card hover:shadow-valia-lg transition-all duration-300 ease-in-out p-4 sm:p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="relative flex-shrink-0">
                    <div
                      className={`w-12 h-12 rounded-lg ${getActionColor(
                        log.action
                      )} flex items-center justify-center shadow-lg`}
                    >
                      {getActionIcon(log.action)}
                    </div>
                    {/* Timeline connector */}
                    {index < filteredAndSortedLogs.length - 1 && (
                      <div className="absolute left-6 top-12 w-0.5 h-8 bg-valia-gray-300"></div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-valia-gray-900 mb-1">
                          {log.action}
                        </h3>
                        <div className="flex items-center gap-2 text-sm text-valia-gray-600">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                            />
                          </svg>
                          <span>Performed by</span>
                          <span className="font-semibold text-valia-primary">
                            {log.created_by_name}
                          </span>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="bg-valia-gray-50 rounded-valia px-3 py-2 border border-valia-gray-200">
                          <div className="text-valia-gray-700 text-sm font-semibold">
                            {timestamp.split(",")[0]}
                          </div>
                          <div className="text-valia-gray-600 text-xs">
                            {timestamp.split(",")[1]?.trim()}
                          </div>
                        </div>
                      </div>
                    </div>

                    {log.details && Object.keys(log.details).length > 0 && (
                      <div className="bg-valia-gray-50 rounded-valia p-4 border border-valia-gray-200">
                        <h4 className="text-sm font-semibold text-valia-gray-700 mb-3 flex items-center gap-2">
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                            />
                          </svg>
                          Event Details
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(log.details).map(([key, value]) => (
                            <div
                              key={key}
                              className="flex justify-between items-center text-sm bg-white rounded-valia px-3 py-2 border border-valia-gray-200"
                            >
                              <span className="text-valia-gray-600 font-medium capitalize">
                                {key.replace(/([A-Z])/g, " $1").trim()}:
                              </span>
                              <span
                                className="text-valia-gray-900 text-right max-w-xs truncate font-medium"
                                title={String(value)}
                              >
                                {String(value)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default LogList;
